import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import useToast from '@sdk/common/toast/useToast';
import getErrorForToast from '@sdk/api/utils/getErrorForToast';

import { useShiftTaskAttachmentsFeatureFlag } from '@web/shiftTasks/hooks/useShiftTaskAttachmentsFeatureFlag';
import { TaskInstance } from '@web/shiftTasks/types/TaskInstance';
import { getTaskInstancePayload, mapTaskInstanceToFormValues } from '@web/shiftTasks/utils/mappers';
import { useEditTaskInstance } from '@web/shiftTasks/hooks/useEditTaskInstance';
import { TaskInstanceFormValues } from '@web/shiftTasks/ComposeTask/types/TaskInstanceFormValues';

import { getShiftTaskDate } from './editInstance.utils';

export function useEditTaskInstanceForm(
  originalInstance: TaskInstance,
  onSubmitSuccess: () => void
) {
  const { id: instanceId, dtstart } = originalInstance;

  const editTaskInstanceMutation = useEditTaskInstance(instanceId);
  const { areAttachmentsEnabled } = useShiftTaskAttachmentsFeatureFlag();
  const { showErrorToast } = useToast();
  const taskInstanceFormValues = useMemo(
    () => (originalInstance ? mapTaskInstanceToFormValues(originalInstance) : null),
    [originalInstance]
  );
  const startDate = getShiftTaskDate(dtstart);

  const formMethods = useForm<TaskInstanceFormValues>({ defaultValues: taskInstanceFormValues });
  const onSubmit = formMethods.handleSubmit(async (data) => {
    try {
      const payload = getTaskInstancePayload(instanceId, data);
      await editTaskInstanceMutation.mutateAsync(payload);

      onSubmitSuccess();
    } catch (e) {
      showErrorToast(getErrorForToast(e));
    }
  });

  return {
    areAttachmentsEnabled,
    formMethods,
    startDate,
    onSubmit,
  };
}
